import BackButton from "../components/BackButton";
import { formatFranc } from '../lib/utils';
import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import EntityAvatar from '../components/EntityAvatar';
import { Eye, EyeOff, TrendingUp, Users, X } from 'lucide-react';
import FrancIcon from '../components/FrancIcon';
import { toast } from 'sonner';

export default function WatchlistPage({ onEntityClick, onBack }) {
  const [watchlist, setWatchlist] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.get('/watchlist');
      setWatchlist(res.data.watchlist || []);
      setEntities(res.data.entities || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // v5.7.91 — EntityDetail (the person profile, often opened FROM the
  // Watchlist) dispatches `watchlist:changed` whenever the user toggles
  // watch/unwatch. We listen here so that backing out of EntityDetail
  // lands the user on a FRESH watchlist instead of a stale snapshot.
  // The page stays mounted (display:none) under the EntityDetail
  // overlay in App.js's nav stack, so the mount-time effect doesn't
  // re-run on its own.
  useEffect(() => {
    const onChange = () => { load(); };
    window.addEventListener('watchlist:changed', onChange);
    return () => window.removeEventListener('watchlist:changed', onChange);
  }, []);

  const removeWatch = async (entityId) => {
    try {
      await api.delete(`/watchlist/${entityId}`);
      toast.success('Removed from watchlist');
      load();
    } catch (e) { toast.error('Failed to remove'); }
  };

  if (loading) {
    return <div className="p-4 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-16 w-full" />)}</div>;
  }

  return (
    <div className="p-4" data-testid="watchlist-page">
      {onBack && <BackButton onClick={onBack} />}
      <h2 className="text-lg font-semibold tracking-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
        <Eye className="h-5 w-5 inline mr-2" />
        Watchlist ({entities.length})
      </h2>

      <ScrollArea className="h-[calc(100vh-200px)]">
        <div className="space-y-2">
          {entities.length === 0 && (
            <div className="text-center py-12">
              <Eye className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground text-sm">No people on watchlist yet.</p>
              <p className="text-xs text-muted-foreground mt-1">Visit Person Details and add people to track them.</p>
            </div>
          )}
          {entities.map(entity => (
            <div
              key={entity.id}
              className="flex items-center gap-3 bg-card border border-border/60 rounded-xl px-4 py-3 cursor-pointer hover:shadow-sm"
              onClick={() => onEntityClick?.(entity.id)}
            >
              <EntityAvatar entity={entity} size={40} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{entity.name}</p>
                <p className="text-[10px] text-muted-foreground">{entity.entity_type?.replace(/_/g, ' ')}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="text-right">
                  <p className="text-sm font-bold tabular-nums">{formatFranc(entity.pricing?.price)}</p>
                  {entity.stats?.weekly_points > 0 && (
                    <span className="badge-positive text-[9px] px-1.5 py-0.5 rounded-full">
                      {formatFranc(entity.stats.weekly_points, { sign: true })}
                    </span>
                  )}
                </div>
                <Button
                  size="sm" variant="ghost" className="h-7 text-xs text-destructive"
                  onClick={(e) => { e.stopPropagation(); removeWatch(entity.id); }}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
